import React, { useState } from 'react';
import styles from './WorkerItem.module.scss';
import { useDispatch } from 'react-redux';
import { salonManagementActions } from '../../../../store';
import AvatarIcon from '../../../../../Common/AvatarIcon';
import { ROUTES } from '../../../../../../routes/constants';
import { useNavigate } from 'react-router-dom';
import useWindowSize from '../../../../../../utils/useWindowSize';
import clx from 'classnames';
import { modalActions } from '../../../../../Modals/store';
import HoveredInfoIcon from '../../../../../Common/HoveredInfoIcon';
import Switcher from '../../../../../Common/Switcher';
import { getActiveSalonePaymentInfo } from 'utils/salonToken';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Clock3,
  GripVertical,
  Pencil,
  Scissors,
  UserCheck,
  UserX,
} from 'lucide-react';

export const WorkerItem = ({ worker, isDragged, setWorkerToDeleteId, setWorkerToEnableId }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentDisabled = getActiveSalonePaymentInfo();
  const [avatarImg, setAvatarImg] = useState({});
  const avatarSrc =
    avatarImg.value && avatarImg.id === worker.id
      ? avatarImg.value
      : worker.icon;
  const isDisabled = !!worker.disabilitato;
  const isHiddenInApp = !worker.visible && !isDisabled;

  const openAvatarModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'editWorkerAvatarModal',
        callback: (value) =>
          setAvatarImg({
            id: worker.id,
            value,
          }),
        props: { workerId: worker.id },
      }),
    );
  };

  const openNameModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'editWorkerNameModal',
        props: worker,
      }),
    );
  };

  const openDescriptionModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'editWorkerDescriptionModal',
        props: worker,
      }),
    );
  };

  return (
    <details className={clx(styles.item, { [styles.item_mobile]: isMobile })}>
      <summary className={styles.item_summary}>
        <button
          type="button"
          data-movable-handle
          className={styles.drag_handle}
          style={{ cursor: isDragged ? 'grabbing' : 'grab' }}
          tabIndex={-1}
          aria-label={t('workers.workerItem.drag')}
          onClick={(event) => event.preventDefault()}
        >
          <GripVertical size={18} strokeWidth={2.2} />
        </button>

        <AvatarIcon name={worker.name} icon={avatarSrc} size="40px" />

        <div className={styles.summary_copy}>
          <div className={styles.item_summary_name}>{worker.name}</div>
          {(isHiddenInApp || isDisabled) && (
            <div className={styles.summary_meta}>
              {isHiddenInApp && (
                <span className={styles.meta_pill}>{t('workers.workerItem.hiddenInApp')}</span>
              )}
              {isDisabled && (
                <span className={clx(styles.meta_pill, styles.meta_pill_disabled)}>
                  {t('workers.workerItem.disabled')}
                </span>
              )}
            </div>
          )}
        </div>
        <div className={styles.item_summary_icon}>
          <ChevronRight size={18} strokeWidth={2.2} />
        </div>
      </summary>

      <div className={styles.item_container}>
        <div className={styles.info_card}>
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('workers.workerItem.photo')}</span>
            <div className={styles.info_value}>
              <AvatarIcon name={worker.name} icon={avatarSrc} size="36px" />
              <button
                type="button"
                className={styles.icon_action}
                onClick={openAvatarModal}
                aria-label={t('workers.workerItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('workers.workerItem.chairName')}</span>
            <div className={styles.info_value}>
              <span>{worker.name}</span>
              <button
                type="button"
                className={styles.icon_action}
                onClick={openNameModal}
                aria-label={t('workers.workerItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('workers.workerItem.chairDescription')}</span>
            <div className={styles.info_value}>
              <span>{worker.description || '—'}</span>
              <button
                type="button"
                className={styles.icon_action}
                onClick={openDescriptionModal}
                aria-label={t('workers.workerItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
        </div>

        <div
          className={clx(styles.settings_row, {
            [styles.settings_row_single]: paymentDisabled,
          })}
        >
          <div className={styles.setting_block}>
            <div className={styles.setting_title}>
              {t('workers.workerItem.visibleInApp')}
              <HoveredInfoIcon description={t('workers.workerItem.visibleInAppInfo')} />
            </div>
            <Switcher
              value={!worker.visible}
              setValue={(value) => {
                dispatch(
                  salonManagementActions.EDIT_WORKER.REQUEST({
                    idPoltrona: worker.id,
                    visible: +value,
                  }),
                );
              }}
              isFullWidth={isMobile}
            />
          </div>
          {!paymentDisabled && (
            <div className={styles.setting_block}>
              <div className={styles.setting_title}>
                {t('workers.workerItem.onlinePaymentRequired')}
                <HoveredInfoIcon description={t('workers.workerItem.onlinePaymentInfo')} />
              </div>
              <div className={styles.payment_badge}>{t('workers.workerItem.notActive')}</div>
            </div>
          )}
        </div>

        <div className={styles.nav_cards}>
          <button
            type="button"
            className={styles.nav_card}
            onClick={() =>
              navigate(
                `${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.WORKING_TIME}/${worker.id}`,
              )
            }
          >
            <span className={styles.nav_card_icon}>
              <Clock3 size={18} strokeWidth={2.2} />
            </span>
            <span className={styles.nav_card_copy}>
              <span className={styles.nav_card_title}>{t('workers.workerItem.schedule')}</span>
              <span className={styles.nav_card_value}>
                {t('workers.workerItem.scheduleDescription')}
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            className={styles.nav_card}
            onClick={() =>
              navigate(`${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.SERVICES}/${worker.id}`)
            }
          >
            <span className={styles.nav_card_icon}>
              <Scissors size={18} strokeWidth={2.2} />
            </span>
            <span className={styles.nav_card_copy}>
              <span className={styles.nav_card_title}>{t('workers.workerItem.services')}</span>
              <span className={styles.nav_card_value}>
                {t('workers.workerItem.servicesDescription')}
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className={styles.footer}>
          <div className={styles.footer_copy}>
            <div className={styles.footer_title_row}>
              <span className={styles.footer_title}>
                {isDisabled
                  ? t('workers.workerItem.enableChair')
                  : t('workers.workerItem.disableChair')}
              </span>
              {!isDisabled && (
                <HoveredInfoIcon description={t('workers.workerItem.disableChairInfo')} />
              )}
            </div>
            {!isDisabled && (
              <p className={styles.footer_hint}>{t('workers.workerItem.disableHint')}</p>
            )}
          </div>
          {!isDisabled ? (
            <button
              type="button"
              onClick={() => setWorkerToDeleteId(worker.id)}
              className={styles.delete_button}
            >
              <UserX size={16} strokeWidth={2.2} aria-hidden="true" />
              {t('common.actions.disable')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setWorkerToEnableId(worker.id)}
              className={styles.enable_button}
            >
              <UserCheck size={16} strokeWidth={2.2} aria-hidden="true" />
              {t('common.actions.enable')}
            </button>
          )}
        </div>
      </div>
    </details>
  );
};
